import { Icon } from '@vrplatform/icons';
import {
  Anchor,
  Box,
  Collapse,
  Text,
  rgba,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { NavLink } from 'react-router';
import { useNavbarExpanded } from '../../hooks';
import { useStyles } from './_styles';
import type { IParentRoute } from './_types';

type Props = IParentRoute & {
  active?: boolean;
  collapsible: NonNullable<IParentRoute['collapsible']>;
};

// A parent that only expands and collapses its sub routes and remembers it.
// The icon-only navbar shows a link to the group with a tooltip.
export const NavbarCollapsibleRoute = ({
  title,
  icon,
  subRoutes,
  link,
  active,
  collapsible,
}: Props) => {
  const { classes, cx } = useStyles();
  const { colorScheme } = useMantineColorScheme();
  const { isDesktopExpanded } = useNavbarExpanded();
  const [opened, setOpened] = useLocalStorage({
    key: collapsible.storageKey,
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const parentClassName = cx(classes.navLink, active ? 'active' : undefined);

  if (!isDesktopExpanded) {
    return (
      <Tooltip label={title} position="right" withArrow={false}>
        <Anchor
          component={NavLink}
          to={link ?? ''}
          aria-label={title}
          className={parentClassName}
          sx={{ display: 'block', textDecoration: 'none' }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
            })}
          >
            {icon}
          </Box>
        </Anchor>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        '.route': {
          color:
            colorScheme === 'dark'
              ? theme.colors.neutral[3]
              : theme.colors.neutral[0],
          fontSize: theme.fontSizes.sm,
        },
        // the parent is a button anchor, the sub routes are links
        '& a, & a:hover, & button, & button:hover': {
          textDecoration: 'none',
        },
      })}
    >
      <Anchor
        component="button"
        type="button"
        aria-expanded={opened}
        className={parentClassName}
        onClick={() => setOpened(!opened)}
        sx={{ width: '100%', textAlign: 'left' }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing.xs,
            padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              flex: 1,
              minWidth: 0,
            })}
          >
            {icon}
            <Text
              component="span"
              className="route"
              sx={{ flex: 1, minWidth: 0, lineHeight: 'normal' }}
            >
              {title}
            </Text>
          </Box>
          <Icon
            icon="ChevronIcon"
            size={14}
            color="white"
            sx={{
              transform: opened ? 'rotate(180deg)' : undefined,
              transition: 'transform 120ms ease',
            }}
          />
        </Box>
      </Anchor>
      <Collapse expanded={opened}>
        {/* a guide line under the parent's icon, the sub routes beside it */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginLeft: 19,
            marginBottom: theme.spacing.xs,
            paddingLeft: theme.spacing.xs,
            borderLeft: `1px solid ${theme.colors.vrplatform[7]}`,
          })}
        >
          {subRoutes?.map((subRoute) => (
            <Anchor
              key={subRoute.id}
              component={NavLink}
              to={`${subRoute.group}/${subRoute.url}`}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing.xs,
                minHeight: 32,
                paddingInline: theme.spacing.xs,
                borderRadius: theme.radius.md,
                fontSize: theme.fontSizes.xs,
                color: theme.colors.vrplatform[1],
                whiteSpace: 'nowrap',
                // no ring after a click; the keyboard ring stays inside the item
                outline: 'none',
                '&:focus-visible': {
                  outline: `2px solid ${theme.colors.vrplatform[4]}`,
                  outlineOffset: -2,
                },
                '&:hover': {
                  textDecoration: 'none',
                  backgroundColor: rgba(theme.white, 0.08),
                },
                '&.active': {
                  backgroundColor: rgba(theme.white, 0.14),
                  color: theme.white,
                  fontWeight: 500,
                },
              })}
            >
              {subRoute.title}
              {!!subRoute.count && (
                <Box
                  component="span"
                  sx={(theme) => ({
                    backgroundColor: theme.colors.orange[6],
                    color: theme.white,
                    borderRadius: 8,
                    fontSize: 10.5,
                    fontWeight: 600,
                    lineHeight: '16px',
                    paddingInline: 6,
                    minWidth: 18,
                    textAlign: 'center',
                    flex: 'none',
                  })}
                >
                  {subRoute.count}
                </Box>
              )}
            </Anchor>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
